/**
 * Clinic detail v2 → סקירה → "הודעות": 30-day counts per channel (router
 * sends only), fallback + failed counts, last 20 rows. Read-only, no toggles
 * (channel policy is global — whatsapp_integration.md rule 5).
 * Error code: CK-10517 (RTK logs CK-7409).
 */
import { ChatsCircle, WhatsappLogo, ChatText, Warning } from "@phosphor-icons/react";
import ContentLoader from "../../../../../components/shared/ContentLoader";
import { useGetClinicMessagingQuery } from "../../../../../store/services/nivApi";
import MessagingStatusBadge from "../../../../../components/ui/MessagingStatusBadge";
import { MESSAGE_TYPES, describeReason, formatWhen, maskPhone, stampFromRow } from "../../messages/messagesShared";

function Stat({ icon: Icon, value, label, tone = "" }) {
  return (
    <div className="ncd2-stat">
      <span className={`ncd2-card-icon ${tone}`}>
        <Icon size={14} weight="bold" />
      </span>
      <div className="ncd2-stat-info">
        <span className="ncd2-stat-value">{value}</span>
        <span className="ncd2-stat-label">{label}</span>
      </div>
    </div>
  );
}

export default function MessagingSection({ clinicId }) {
  const { data, isLoading, isError } = useGetClinicMessagingQuery({ clinicId, days: 30, limit: 20 }, { skip: !clinicId });
  const counts = data?.counts || { whatsapp: 0, smsFallback: 0, smsDirect: 0, failed: 0, total: 0 };
  const rows = data?.rows || [];

  return (
    <section className="ncd2-card ncd2-messaging">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <ChatsCircle size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">30 יום · וואטסאפ ראשי, SMS גיבוי</span>
          <h3 className="ncd2-card-title">הודעות</h3>
        </div>
        {counts.total > 0 && <span className="ncd2-card-count">{counts.total}</span>}
      </header>

      {isLoading ? (
        <ContentLoader row size="xs" message="טוען…" />
      ) : isError ? (
        <p className="ncd2-muted">לא ניתן לטעון נתוני הודעות.</p>
      ) : (
        <>
          <div className="ncd2-stat-grid">
            <Stat icon={WhatsappLogo} value={counts.whatsapp} label="וואטסאפ" />
            <Stat icon={ChatText} value={counts.smsFallback} label="SMS גיבוי" tone={counts.smsFallback ? "is-warn" : "is-muted"} />
            <Stat icon={ChatText} value={counts.smsDirect} label="SMS ישיר" tone="is-muted" />
            <Stat icon={Warning} value={counts.failed} label="לא נשלחו" tone={counts.failed ? "is-danger" : "is-muted"} />
          </div>
          {rows.length === 0 ? (
            <p className="ncd2-muted">עדיין אין שליחות דרך הראוטר לקליניקה זו (ההודעות הקיימות נשלחות עדיין ב-SMS הישן).</p>
          ) : (
            <ul className="ncd2-msg-list">
              {rows.map((r) => (
                <li key={`${r.source}-${r.id}`} className="ncd2-msg-row">
                  <span className="ncd2-msg-type">{MESSAGE_TYPES[r.messageType]?.label || r.messageType || r.templateId || "—"}</span>
                  <MessagingStatusBadge messaging={stampFromRow(r)} />
                  {r.fallbackReason && <span className="ncd2-msg-reason">{describeReason(r.fallbackReason)}</span>}
                  <span className="ncd2-msg-meta" dir="ltr">{maskPhone(r.to)}</span>
                  <span className="ncd2-msg-meta">{formatWhen(r.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
